import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, Upload, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

import {
  getProposalImageLibrary, addProposalLibraryImage, removeProposalLibraryImage,
} from "../api/prospectingReportsApi";

const TABS = [
  ["all", "All"], ["skyline", "City skylines"], ["logo", "Collings logos"],
  ["colour", "Plain colours"], ["photo", "Uploaded"],
];

/**
 * Pick an image from the shared library (CG QA, 27 Oct): built-in city
 * skylines, Collings logos and plain brand colours, plus photos an admin has
 * added. Admins can upload to / remove from the library here.
 */
const ImageLibraryModal = ({ onClose, onPick }) => {
  const [rows, setRows] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => getProposalImageLibrary()
    .then(({ data }) => { setRows(data?.results || []); setCanManage(!!data?.can_manage); })
    .catch(() => toast.error("Could not load the image library."))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      await addProposalLibraryImage(file);
      toast.success("Added to the library.");
      setTab("photo");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };
  const remove = async (img) => {
    try { await removeProposalLibraryImage(img.id); setRows((r) => r.filter((x) => x.id !== img.id)); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove it."); }
  };

  const shown = rows.filter((r) => tab === "all" || r.category === tab);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Choose from the image library</h3>
            <p className="text-sm text-gray-500">City skylines, Collings logos and plain colours, ready to use.</p>
          </div>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="px-5 pt-4 flex flex-wrap items-center gap-2">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold ${tab === k ? "bg-[#0f8d9b] text-white" : "bg-gray-100 text-gray-600"}`}>{label}</button>
          ))}
          {canManage && (
            <button onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading}
              className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 disabled:opacity-50">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Add to library
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onFile} />
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? <div className="flex justify-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
            shown.length === 0 ? <p className="text-sm text-gray-400">Nothing here yet.</p> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {shown.map((img) => (
                  <div key={img.id} className="relative group">
                    <button type="button" onClick={() => onPick(img.image_url)} title={img.label}
                      className="block w-full rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-[#0f8d9b]">
                      <div className={`h-28 flex items-center justify-center ${img.category === "logo" ? (img.label.includes("white") ? "bg-[#1f2a44]" : "bg-white") : ""}`}
                        style={img.category === "colour" ? { background: img.colour } : undefined}>
                        {img.category !== "colour" && (
                          <img src={img.image_url} alt={img.label}
                            className={img.category === "logo" ? "max-h-16 max-w-[85%] object-contain" : "w-full h-28 object-cover"} />
                        )}
                      </div>
                      <span className="block px-2 py-1 text-xs text-gray-600 text-left truncate">{img.label}</span>
                    </button>
                    {canManage && !img.is_system && (
                      <button onClick={() => remove(img)} aria-label="Remove from library"
                        className="absolute top-1 right-1 p-1 rounded bg-white/90 text-gray-500 hover:text-red-600 opacity-0 group-hover:opacity-100">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageLibraryModal;
